// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Accordion,
  Group,
  Text,
  Loader,
  Center,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  ActionIcon,
  Tooltip,
  Paper,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconShieldCheck,
  IconClock,
  IconCalendar,
  IconCoffee,
  IconBellRinging,
  IconStarFilled,
  IconCategory,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { FilterBar, type FilterConfig } from '../../components/FilterBar';
import { EmptyState } from '../../components/EmptyState';
import {
  usePolicies,
  useCreatePolicy,
  useDeletePolicy,
} from '../../hooks/usePolicies';

interface Policy {
  id: string;
  policyType: string;
  name: string;
  description: string;
  status: string;
}

const POLICY_TYPE_OPTIONS = [
  { value: 'overtime', label: 'Overtime' },
  { value: 'accrual', label: 'Accrual' },
  { value: 'break', label: 'Break' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'premium', label: 'Premium' },
];

const POLICY_TYPE_ICONS: Record<string, React.ReactNode> = {
  overtime: <IconClock size={18} />,
  accrual: <IconCalendar size={18} />,
  break: <IconCoffee size={18} />,
  holiday: <IconStarFilled size={18} />,
  attendance: <IconBellRinging size={18} />,
  premium: <IconShieldCheck size={18} />,
};

export function PolicyListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  // Create modal state
  const [createOpen, setCreateOpen] = useState(false);

  // Delete modal state
  const [deleteTarget, setDeleteTarget] = useState<Policy | null>(null);
  const [deleteReason, setDeleteReason] = useState('');

  const policyTypeFilter = filterValues.policyType || undefined;

  const { data: rawPolicies, loading: isLoading } = usePolicies(policyTypeFilter);
  const data = (rawPolicies ?? []) as unknown as Policy[];

  const createMutation = useCreatePolicy();
  const deleteMutation = useDeletePolicy();

  const createForm = useForm({
    initialValues: {
      policyType: '',
      name: '',
      description: '',
      config: '',
    },
    validate: {
      policyType: (v: string) => (v ? null : 'Policy type is required'),
      name: (v: string) => (v.trim() ? null : 'Name is required'),
      config: (v: string) => {
        if (!v.trim()) return null; // config is optional
        try {
          JSON.parse(v);
          return null;
        } catch {
          return 'Config must be valid JSON';
        }
      },
    },
  });

  const filters: FilterConfig[] = [
    {
      key: 'policyType',
      label: 'Policy Type',
      options: POLICY_TYPE_OPTIONS,
      clearable: true,
    },
  ];

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const openCreate = () => {
    createForm.reset();
    setCreateOpen(true);
  };

  const handleCreate = createForm.onSubmit((values) => {
    const input: {
      policyType: string;
      name: string;
      description?: string;
      config?: Record<string, unknown>;
    } = {
      policyType: values.policyType,
      name: values.name.trim(),
    };
    if (values.description.trim()) {
      input.description = values.description.trim();
    }
    if (values.config.trim()) {
      try {
        input.config = JSON.parse(values.config);
      } catch {
        // validated above
      }
    }

    createMutation.mutate(input)
      .then(() => {
        notifications.show({
          title: 'Policy Created',
          message: `"${input.name}" created as v1 (append-only).`,
          color: 'green',
        });
        setCreateOpen(false);
        createForm.reset();
      })
      .catch((err: unknown) => {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to create policy',
          color: 'red',
        });
      });
  });

  const handleDelete = () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    deleteMutation.mutate({ id: deleteTarget.id })
      .then(() => {
        notifications.show({
          title: 'Policy Deactivated',
          message: 'Policy soft-deleted (new inactive version created).',
          color: 'green',
        });
        setDeleteTarget(null);
        setDeleteReason('');
      })
      .catch((err: unknown) => {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to delete policy',
          color: 'red',
        });
      });
  };

  const groupedPolicies = useMemo(() => {
    if (!data || data.length === 0) return {};

    const filtered = search
      ? data.filter(
          (p: Policy) =>
            p.name.toLowerCase().includes(search.toLowerCase()) ||
            p.description?.toLowerCase().includes(search.toLowerCase()),
        )
      : data;

    const groups: Record<string, Policy[]> = {};
    for (const policy of filtered) {
      const type = policy.policyType || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(policy);
    }
    return groups;
  }, [data, search]);

  const sortedTypes = useMemo(() => {
    return Object.keys(groupedPolicies).sort();
  }, [groupedPolicies]);

  const policyTypeLabel = (type: string) => {
    const opt = POLICY_TYPE_OPTIONS.find((o) => o.value === type);
    return opt?.label || type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Policies"
        subtitle={data ? `${data.length} policies` : undefined}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={openCreate}>
            New Policy
          </Button>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search policies..."
        filters={filters}
        onFilterChange={handleFilterChange}
        filterValues={filterValues}
      />

      {isLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : sortedTypes.length === 0 ? (
        <EmptyState
          icon={<IconCategory size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="No Policies Found"
          message="No policies match your current filters."
        />
      ) : (
        <Accordion multiple variant="separated" defaultValue={sortedTypes}>
          {sortedTypes.map((type) => (
            <Accordion.Item key={type} value={type}>
              <Accordion.Control>
                <Group gap="sm">
                  {POLICY_TYPE_ICONS[type] || <IconShieldCheck size={18} />}
                  <Text fw={600}>{policyTypeLabel(type)}</Text>
                  <Text size="sm" c="dimmed">
                    ({groupedPolicies[type]?.length ?? 0})
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="xs">
                  {(groupedPolicies[type] ?? []).map((policy) => (
                    <Group
                      key={policy.id}
                      justify="space-between"
                      p="sm"
                      style={{
                        borderRadius: 'var(--mantine-radius-sm)',
                        cursor: 'pointer',
                      }}
                      className="mantine-hover"
                    >
                      <div
                        style={{ flex: 1, minWidth: 0 }}
                        onClick={() => navigate(`/policies/${policy.id}`)}
                      >
                        <Text size="sm" fw={500}>
                          {policy.name}
                        </Text>
                        {policy.description && (
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {policy.description}
                          </Text>
                        )}
                      </div>
                      <Group gap="sm">
                        <StatusBadge status={policy.status} />
                        <Tooltip label="Deactivate (soft-delete)">
                          <ActionIcon
                            variant="light"
                            size="sm"
                            color="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(policy);
                            }}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {/* ── Create Policy Modal ──────────────────────────────────────── */}
      <Modal
        opened={createOpen}
        onClose={() => {
          setCreateOpen(false);
          createForm.reset();
        }}
        title="Create Policy"
        size="lg"
      >
        <form onSubmit={handleCreate}>
          <Stack gap="md">
            <Select
              label="Policy Type"
              placeholder="Select policy type"
              required
              data={POLICY_TYPE_OPTIONS}
              {...createForm.getInputProps('policyType')}
            />

            <TextInput
              label="Name"
              placeholder="e.g. California Overtime"
              required
              {...createForm.getInputProps('name')}
            />

            <Textarea
              label="Description"
              placeholder="Optional description of this policy..."
              minRows={2}
              {...createForm.getInputProps('description')}
            />

            <Textarea
              label="Configuration (JSON)"
              placeholder='{"key": "value"}'
              minRows={4}
              autosize
              maxRows={12}
              styles={{ input: { fontFamily: 'monospace' } }}
              {...createForm.getInputProps('config')}
            />

            <Paper p="sm" bg="blue.0" radius="sm">
              <Text size="xs" c="blue.7">
                Append-only: This policy will be created as version 1 with active status.
              </Text>
            </Paper>

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setCreateOpen(false);
                  createForm.reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={createMutation.isPending}>
                Create Policy
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* ── Delete Confirmation Modal ────────────────────────────────── */}
      <Modal
        opened={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteReason('');
        }}
        title="Deactivate Policy"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will soft-delete{' '}
            <Text span fw={600}>
              {deleteTarget?.name}
            </Text>{' '}
            by creating a new inactive version (append-only). The original record is preserved
            for audit.
          </Text>
          <Textarea
            label="Reason for deactivation"
            placeholder="Required for audit trail..."
            required
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.currentTarget.value)}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteReason('');
              }}
            >
              Keep Policy
            </Button>
            <Button
              color="red"
              disabled={!deleteReason.trim()}
              loading={deleteMutation.isPending}
              onClick={handleDelete}
            >
              Deactivate Policy
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
